import React, { useEffect, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Help } from '@mui/icons-material';
import { getPrice, priceToString } from 'shared/utils';
import StyledSwitch from 'shared/components/styled/switch';
import { TooltipIcon } from 'shared/components/abstract/interactables';
import { ICSDesktopDatePicker } from 'shared/components/styled/editors';
import { maxDateStr, minDateStr } from './helper';

/**
 * @param {Object} props
 * @param {Function} props.validatorFunc - Validation function that returns boolean
 * @param {string} props.helperText - Helper text to display when validation fails
 * @param {Function} [props.setValid] - Optional callback to set valid state
 * @returns {Function} DataValidator component
 */
export function withDataValidator({ validatorFunc, helperText, setValid = () => {} }) {
  /**
   *
   * @param {*} value
   * @returns {{message: string, valid: *}}
   * @constructor
   */
  function DataValidator(value) {
    setValid(validatorFunc(value));
    return { message: helperText, valid: validatorFunc(value) };
  }
  return DataValidator;
}

const handleOnBlur = (event, setValue, onBlur) => {
  const newValue = event.target.value;
  if (newValue?.length > 0) {
    setValue(newValue.trim());
  }
  if (typeof onBlur instanceof Function || typeof onBlur === 'function') {
    onBlur(event);
  }
};

/**
 * @param {Object} props
 * @param {boolean} [props.fullWidth=true] - Whether the input should take full width
 * @param {string} props.label - Label for the input field
 * @param {Function} props.setValue - Function to update the value
 * @param {string|number|boolean} [props.value] - Current value
 * @param {string} [props.helperText=''] - Helper text to display below input
 * @param {boolean} [props.required] - Whether the field is required
 * @param {Function} [props.onBlur] - onBlur handler
 * @param {Object} [props.formHelperTextProps] - Props to pass to FormHelperText
 * @param {string|React.ReactNode} props.tooltipText - Text or node to display in tooltip
 * @param {Object} props.rest - Additional props to pass to OutlinedInput
 */
export function TextEditorWithHelpIcon({
  fullWidth = true,
  label,
  setValue,
  value,
  helperText = '',
  required,
  onBlur,
  formHelperTextProps,
  tooltipText,
  ...rest
}) {
  const handleChange = (event) => {
    setValue(event.target.value);
  };

  return (
    <FormControl variant="outlined" fullWidth={fullWidth}>
      <InputLabel shrink required={required}>{label}</InputLabel>
      <OutlinedInput
        notched
        endAdornment={(
          <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
            <TooltipIcon icon={Help} message={tooltipText} color="primary" />
          </InputAdornment>
      )}
        inputProps={{ style: { padding: '.53125rem 1rem' } }}
        label={label}
        onChange={handleChange}
        required={required}
        value={value}
        slotProps={{ inputLabel: { shrink: true } }}
        onBlur={(event) => handleOnBlur(event, setValue, onBlur)}
        {...rest}
      />
      <FormHelperText {...formHelperTextProps}>
        {helperText}
      </FormHelperText>
    </FormControl>
  );
}

/**
 * Validation is optional.
 * @param {Object} props
 * @param {boolean} [props.fullWidth=true] - Whether the input should take full width
 * @param {string} props.label - Label for the input field
 * @param {Function} props.setValue - Function to update the value
 * @param {string|number|boolean} [props.value] - Current value
 * @param {boolean} [props.required] - Whether the field is required
 * @param {Function} [props.onBlur] - onBlur handler
 * @param {Object} [props.formHelperTextProps] - Props to pass to FormHelperText
 * @param {Function} [props.validator] - Validation function
 * @param {React.ReactNode} [props.helperText] - Helper text to display below input
 * @param {Object} props.rest - Additional props to pass to TextField
 */
export function TextEditor({
  fullWidth = true,
  label,
  setValue,
  value,
  required,
  onBlur,
  formHelperTextProps,
  validator,
  helperText,
  ...rest
}) {
  const [error, setError] = useState(false);
  const [editorHelperText, setHelperText] = useState(validator ? (helperText || ' ') : helperText);
  const ref = useRef(undefined);
  const validationHandler = () => {
    if (value) {
      // If there is a value validate it
      const { valid, message } = validator(value);

      // if that value is valid, immediately set validation
      if (valid) {
        clearTimeout(ref?.current?.timeout);
        ref.current = undefined;
        setError(false);
        setHelperText(helperText || ' ');
        return;
      }
      // if validation time has elapsed AND the value is invalid, set the error and helperText
      setError(true);
      setHelperText(message);
      return;
    }
    setHelperText(helperText || ' ');
    if (ref.current) {
      ref.current = undefined;
    }
    if (error) {
      setError(false);
      setHelperText(helperText || ' ');
    }
  };
  useEffect(() => {
    if (validator) {
      validationHandler();
    }
    // validationHandler is recreated each render and sets error/helperText state it reads;
    // depending on it would loop. Validation is intentionally re-run only when value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (event) => {
    setValue(event.target.value);
  };

  return (
    <TextField
      error={error}
      className="input-field"
      fullWidth={fullWidth}
      label={label}
      size="small"
      value={value}
      onChange={handleChange}
      helperText={editorHelperText}
      required={required}
      slotProps={{
        formHelperText: formHelperTextProps,
        inputLabel: { shrink: true },
        ...rest.slotProps,
      }}
      onBlur={(event) => handleOnBlur(event, setValue, onBlur)}
      {...rest}
    />
  );
}

/**
 * @param {Object} props
 * @param {boolean} [props.fullWidth=true] - Whether the input should take full width
 * @param {string} props.label - Label for the input field
 * @param {Function} props.setValue - Function to update the value
 * @param {string|number|boolean} [props.value] - Current value
 * @param {Function} [props.onBlur] - onBlur handler
 */
export function TextAreaEditor({
  fullWidth = true, label, setValue, value, onBlur,
}) {
  return (
    <TextField
      className="input-field"
      fullWidth={fullWidth}
      multiline
      label={label}
      size="small"
      slotProps={{ inputLabel: { shrink: true } }}
      value={value}
      onChange={({ target: { value: newValue } }) => setValue(newValue)}
      onBlur={(event) => handleOnBlur(event, setValue, onBlur)}
    />
  );
}

/**
 * Validates its value.
 * @param {Object} props
 * @param {boolean} [props.fullWidth=true] - Whether the input should take full width
 * @param {string} props.label - Label for the input field
 * @param {Function} props.setValue - Function to update the value
 * @param {string|number|boolean} [props.value] - Current value
 * @param {number} [props.minimum=1] - Minimum allowed value
 * @param {number} [props.maximum=null] - Maximum allowed value
 * @param {Object} [props.startAdornment=null] - Start adornment component
 * @param {Object} [props.endAdornment=null] - End adornment component
 * @param {boolean} [props.disabled=false] - Whether the input is disabled
 * @param {boolean} [props.required=false] - Whether the field is required
 * @param {Function} [props.onBlur] - onBlur handler
 */
export function NumberEditor({
  fullWidth = true,
  label,
  setValue,
  value,
  minimum = 1,
  maximum = null,
  startAdornment = null,
  endAdornment = null,
  disabled = false,
  required = false,
  onBlur,
}) {
  const handleKeyDown = (event) => {
    const disabledKeys = ['-', '+', 'e', '.'];
    if (disabledKeys.includes(event?.key) || (event?.code === 'ArrowDown' && value === 0)) {
      event.preventDefault();
    }
  };

  const onChange = ({ target: { value: newValue } }) => {
    let valueToSet = newValue;
    if (newValue < minimum) valueToSet = null;
    if (maximum && newValue > maximum) valueToSet = maximum;
    setValue(parseInt(valueToSet, 10));
  };

  return (
    <TextField
      disabled={disabled}
      className="input-field"
      fullWidth={fullWidth}
      label={label}
      size="small"
      required={required}
      value={value?.toString() || value}
      onChange={onChange}
      onKeyDown={handleKeyDown}
      onWheel={(event) => {
        event.target.blur();
      }}
      slotProps={{
        input: {
          inputMode: 'numeric',
          pattern: '[0-9]*',
          min: minimum,
          max: maximum,
          startAdornment,
          endAdornment,
        },
        inputLabel: { shrink: true },
      }}
      type="number"
      onBlur={onBlur}
    />
  );
}

/**
 * @param {Object} props
 * @param {boolean} [props.fullWidth=true] - Whether the input should take full width
 * @param {string} props.label - Label for the checkbox
 * @param {boolean} [props.value=false] - Current checked state
 * @param {Function} props.setValue - Function to update the value
 * @param {string} [props.labelPlacement='start'] - Position of the label.
 *   One of: 'start', 'end', 'top', 'bottom'
 * @param {boolean} [props.disabled=false] - Whether the checkbox is disabled
 */
export function CheckboxEditor({
  fullWidth = true,
  label,
  value = false,
  setValue,
  labelPlacement = 'start',
  disabled = false,
}) {
  const onChange = (event) => setValue(event.target.checked);
  return (
    <FormControlLabel
      sx={fullWidth ? { width: '100%' } : {}}
      className="input-field"
      control={<Checkbox checked={!!value} onChange={onChange} size="small" />}
      label={label}
      disabled={disabled}
      labelPlacement={labelPlacement}
    />
  );
}

/**
 * @param {Object} props
 * @param {boolean|number|string|Object} [props.value] - Current date value (dayjs object or string)
 * @param {Function} props.setValue - Function to update the value
 * @param {string} [props.label=''] - Label for the date picker
 * @param {string|Object} [props.minDate] - Minimum selectable date (dayjs object or string)
 * @param {string|Object} [props.maxDate] - Maximum selectable date (dayjs object or string)
 */
export function DateEditor({
  value,
  setValue,
  label = '',
  minDate = minDateStr,
  maxDate = maxDateStr,
}) {
  const priorToMinDate = value === undefined || (dayjs(value) < dayjs(minDateStr));

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale('en')}>
      <ICSDesktopDatePicker
        showDaysOutsideCurrentMonth
        value={priorToMinDate ? null : dayjs(value)}
        label={label}
        onChange={(newValue) => setValue(newValue || undefined)}
        slotProps={{
          textField: { size: 'small', fullWidth: true, slotProps: { inputLabel: { shrink: true } } },
        }}
        maxDate={value ? dayjs(maxDate) : null}
        minDate={value ? dayjs(minDate) : null}
      />
    </LocalizationProvider>
  );
}

/**
 * Higher-order function that creates a date editor for filtering with string output
 * @returns {Function} FilterDateEditor component
 */
export function withDateEditorFilter() {
  function FilterDateEditor({ setValue, ...props }) {
    const setFilterDateValue = (value) => {
      if (value) {
        return setValue(value.format('YYYY-MM-DD'));
      }
      return setValue(value);
    };
    return <DateEditor setValue={setFilterDateValue} {...props} />;
  }

  return FilterDateEditor;
}

/**
 * Price editor for Stripe amounts (cents to dollars conversion)
 * @param {Object} props
 * @param {boolean} [props.fullWidth=true] - Whether the input should take full width
 * @param {string} props.label - Label for the input field
 * @param {boolean} [props.required=false] - Whether the field is required
 * @param {Function} props.setValue - Function to update the value
 * @param {number} [props.value=0] - Current price value in cents
 */
export function StripePriceEditor({
  fullWidth = true,
  label,
  required = false,
  setValue,
  value = 0,
}) {
  const [rawValue, setRawValue] = useState(priceToString(value));

  useEffect(() => {
    setRawValue(priceToString(value));
  }, [value]);

  const setPriceValue = () => {
    const { value: priceValue, formatted } = getPrice(rawValue);
    setValue(priceValue);
    setRawValue(formatted);
  };
  const handleKeyDown = (event) => {
    if (event?.code === 'Enter') {
      event.preventDefault();
      setPriceValue();
    }
  };

  return (
    <TextField
      className="input-field"
      fullWidth={fullWidth}
      label={label}
      size="small"
      value={rawValue}
      onChange={({ target: { value: newValue } }) => setRawValue(newValue)}
      onBlur={(event) => handleOnBlur(event, setRawValue, setPriceValue())}
      onKeyDown={handleKeyDown}
      required={required}
      slotProps={{
        input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
        inputLabel: { shrink: true },
      }}
    />
  );
}

/**
 * Price editor for string-based price values
 * @param {Object} props
 * @param {boolean} [props.fullWidth=true] - Whether the input should take full width
 * @param {string} props.label - Label for the input field
 * @param {string|number|boolean} [props.value] - Current price value as string
 * @param {Function} props.setValue - Function to update the value
 */
export function StringPriceEditor({
  fullWidth = true, label, value, setValue,
}) {
  const setPriceValue = () => {
    const { formatted } = getPrice(value);
    setValue(formatted);
  };
  const handleKeyDown = (event) => {
    if (event?.code === 'Enter') {
      event.preventDefault();
      setPriceValue();
    }
  };
  return (
    <TextField
      className="input-field"
      fullWidth={fullWidth}
      label={label}
      onKeyDown={handleKeyDown}
      size="small"
      slotProps={{
        input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
        inputLabel: { shrink: true },
      }}
      placeholder="0"
      value={value}
      onChange={({ target: { value: newValue } }) => setValue(newValue)}
      onBlur={(event) => handleOnBlur(event, setValue, setPriceValue())}
    />
  );
}

/**
 * Higher-order function that creates a static select editor from an array of strings
 * Values and labels will be the same
 * @param {Array<string>} options - Array of string options for the select
 * @param {boolean} [required=false] - Whether the field is required
 * @returns {Function} SelectEditor component
 */
export function withStaticSelectEditor(options, required = false) {
  /**
   * @param {Object} props
   * @param {boolean} [props.fullWidth=true] - Whether the input should take full width
   * @param {string} props.label - Label for the select field
   * @param {string|number|boolean} [props.value] - Current selected value
   * @param {Function} props.setValue - Function to update the value
   * @param {boolean} [props.disabled=false] - Whether the select is disabled
   * @param {string} [props.placeholder] - Placeholder text
   * @param {Object} props.rest - Additional props
   */
  function SelectEditor({
    fullWidth = true,
    label,
    value,
    setValue,
    disabled = false,
    placeholder = 'Select an option...',
    ...rest
  }) {
    return (
      <FormControl
        required={required}
        fullWidth={fullWidth}
        size="small"
        className="input-field"
        {...rest}
      >
        <InputLabel shrink>{label}</InputLabel>
        {/* Select draws its value in a div; a `placeholder` prop only reaches the hidden
            native input. `displayEmpty` makes renderValue run for an empty value too. */}
        <Select
          disabled={disabled}
          displayEmpty
          renderValue={(v) => v || <span className="placeholder">{placeholder}</span>}
          fullWidth
          input={<OutlinedInput notched label={label} />}
          value={value || ''}
          onChange={({ target: { value: v } }) => setValue(v)}
          sx={{ height: 45 }}
          variant="outlined"
        >
          {options.map((o) => (
            <MenuItem key={o} value={o}>{o}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return SelectEditor;
}

/**
 * Higher-order function that creates a select editor with value/label objects
 * Options must be shaped like [{ value: 1, label: 'option name'}, ...]
 * @param {Array<{value: *, label: string}>} options - Array of option objects with value and label
 * @param {boolean} [convertInt=false] - Whether to convert the value to integer
 * @param {boolean} [required=false] - Whether the field is required
 * @returns {Function} SelectEditor component
 */
export function withSelectEditor(options, convertInt = false, required = false) {
  /**
   * @param {Object} props
   * @param {boolean} [props.fullWidth=true] - Whether the input should take full width
   * @param {string} props.label - Label for the select field
   * @param {string|number|boolean} [props.value] - Current selected value
   * @param {Function} props.setValue - Function to update the value
   * @param {string} [props.placeholder] - Placeholder text
   */
  function SelectEditor({
    fullWidth = true,
    label,
    value,
    setValue,
    placeholder = 'Select an option...',
  }) {
    return (
      <FormControl
        required={required}
        fullWidth={fullWidth}
        size="small"
        className="input-field"
      >
        <InputLabel shrink>{label}</InputLabel>
        {/* See withStaticSelectEditor: only renderValue can show a placeholder. */}
        <Select
          displayEmpty
          renderValue={(v) => (v === ''
            ? <span className="placeholder">{placeholder}</span>
            // String compare: a converted value arrives as a number, options may be strings.
            : options.find((o) => String(o.value) === String(v))?.label ?? v)}
          input={<OutlinedInput notched label={label} />}
          value={value || ''}
          onChange={({ target: { value: v } }) => setValue(convertInt ? parseInt(v, 10) : v)}
          variant="outlined"
        >
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return SelectEditor;
}

/**
 * Higher-order function that creates a switch/toggle component
 * @returns {Function} SelectorSwitch component
 */
export function withSelectorSwitch() {
  /**
   * @param {Object} props
   * @param {string} props.label - Label for the switch
   * @param {string|boolean} [props.value=false] - Current switch state
   * @param {Function} props.setValue - Function to update the value
   */
  function SelectorSwitch({ label, value = false, setValue }) {
    const switchOption = () => {
      setValue(!value);
    };

    return (
      <FormControlLabel
        control={<StyledSwitch checked={!!value} onChange={switchOption} />}
        label={label}
      />
    );
  }

  return SelectorSwitch;
}
